import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CloudArrowUpIcon,
  MapPinIcon,
  BeakerIcon,
  CubeIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlusIcon,
  TrashIcon,
  SparklesIcon,
  FireIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { FadeIn, StaggeredFadeIn } from '../../components/animations/FadeIn';
import { polygonApi, SiteInput, MonitoringWellInput, HydroData } from '../../api/polygon';
import { envirodataApi, PlumeDataResponse, EnviroDataConfigResponse } from '../../api/envirodata';
import { aiApi, ExtractionResponse, DocumentType, AIStatusResponse, FetchSiteDataResponse, PolygonData, MonitoringWellData, ExcelExtractionResponse, WellInventoryItem } from '../../api/ai';
import { datahubApi, EnrichmentResponse, AutoFillData, WellInfo } from '../../api/datahub';

// Contaminant types with regulatory limits (µg/L)
// For Texas RRC sites, these are RRC Class 1 & 2 Impacted Groundwater Delineation and Remediation Limits
const CONTAMINANT_OPTIONS = [
  // BTEX compounds (RRC Class 1 & 2 limits for Texas)
  { value: 'benzene', label: 'Benzene', mcl: 5, group: 'BTEX' },
  { value: 'toluene', label: 'Toluene', mcl: 1000, group: 'BTEX' },
  { value: 'ethylbenzene', label: 'Ethylbenzene', mcl: 700, group: 'BTEX' },
  { value: 'xylenes', label: 'Xylenes (Total)', mcl: 10000, group: 'BTEX' },
  // TPH (RRC limit 0.98 mg/L = 980 µg/L)
  { value: 'tph', label: 'TPH (Total Petroleum Hydrocarbons)', mcl: 980, group: 'Petroleum' },
  // Other petroleum
  { value: 'mtbe', label: 'MTBE', mcl: 20, group: 'Petroleum' },
  { value: 'tph_gro', label: 'TPH-GRO (Gasoline)', mcl: 500, group: 'Petroleum' },
  { value: 'tph_dro', label: 'TPH-DRO (Diesel)', mcl: 1000, group: 'Petroleum' },
  { value: 'naphthalene', label: 'Naphthalene', mcl: 100, group: 'Petroleum' },
  // Chlorinated solvents
  { value: 'tce', label: 'TCE (Trichloroethylene)', mcl: 5, group: 'Chlorinated' },
  { value: 'pce', label: 'PCE (Tetrachloroethylene)', mcl: 5, group: 'Chlorinated' },
  { value: 'dcee', label: '1,2-DCE', mcl: 70, group: 'Chlorinated' },
  { value: 'vinyl_chloride', label: 'Vinyl Chloride', mcl: 2, group: 'Chlorinated' },
  // Other
  { value: 'pfas', label: 'PFAS (PFOA/PFOS)', mcl: 0.004, group: 'Emerging' },
  { value: 'other', label: 'Other', mcl: 100, group: 'Other' },
];

// Interface for individual contaminant entry
interface ContaminantEntry {
  id: string;
  type: string;
  concentration_mg_l: string;  // User enters in mg/L
  mcl: number;  // MCL stored in ug/L for comparison
}

type CoordinateFormat = 'decimal' | 'dms';

// Helper functions for DMS <-> Decimal conversion
const dmsToDecimal = (degrees: number, minutes: number, seconds: number, direction: 'N' | 'S' | 'E' | 'W'): number => {
  let decimal = Math.abs(degrees) + (minutes / 60) + (seconds / 3600);
  if (direction === 'S' || direction === 'W') {
    decimal = -decimal;
  }
  return decimal;
};

const decimalToDms = (decimal: number, isLat: boolean): { degrees: number; minutes: number; seconds: number; direction: 'N' | 'S' | 'E' | 'W' } => {
  const direction = isLat
    ? (decimal >= 0 ? 'N' : 'S')
    : (decimal >= 0 ? 'E' : 'W');
  const absDecimal = Math.abs(decimal);
  const degrees = Math.floor(absDecimal);
  const minutesDecimal = (absDecimal - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = (minutesDecimal - minutes) * 60;
  return { degrees, minutes, seconds: Math.round(seconds * 1000) / 1000, direction };
};

interface WellFormData {
  well_id: string;
  latitude: string;
  longitude: string;
  distance_from_source_ft: string;
  direction_from_source_degrees: string;
}

export function PolygonUpload() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);

  // Auto-populate state
  const [isFetchingSiteData, setIsFetchingSiteData] = useState(false);
  const [hydroData, setHydroData] = useState<HydroData | null>(null);
  const [autoPopulated, setAutoPopulated] = useState(false);
  const [autoDetectedWells, setAutoDetectedWells] = useState<HydroData['existing_wells']>([]);
  // Use ref to avoid stale closure issues when accessing wells in handleSubmit
  const autoDetectedWellsRef = useRef<HydroData['existing_wells']>([]);

  // EnviroData integration state
  const [envirodataConfig, setEnvirodataConfig] = useState<EnviroDataConfigResponse | null>(null);
  const [envirodataChecked, setEnvirodataChecked] = useState(false);
  const [dataSource, setDataSource] = useState<'envirodata' | 'usgs' | null>(null);
  const [dataSources, setDataSources] = useState<string[]>([]);

  // AI Document Extraction state
  const [aiAvailable, setAiAvailable] = useState(false);
  const [isExtractingDocument, setIsExtractingDocument] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractionResponse | null>(null);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Form state - all fields visible, blank until auto-populated
  const [formData, setFormData] = useState({
    // Site location
    site_name: '',
    latitude: '',
    longitude: '',
    // Plume geometry
    plume_area_sqft: '',
    plume_length_ft: '',
    plume_width_ft: '',
    // PSH/LNAPL source zone data
    psh_present: false,
    psh_thickness_ft: '',
    psh_area_sqft: '',
    psh_volume_gallons: '',
    psh_saturation_percent: '',
    // Remediation
    remediation_type: 'none',
    // Aquifer properties (auto-populated)
    aquifer_k_ft_day: '',
    aquifer_porosity: '',
    hydraulic_gradient: '',
    flow_direction_degrees: '',
    aquifer_thickness_ft: '',
    transmissivity_ft2_day: '', // T = K × b
    // Additional auto-populated fields
    aquifer_name: '',
    depth_to_water_ft: '',
    elevation_ft: '',
    groundwater_velocity_ft_day: '',
    soil_type: '',
    organic_carbon_foc: '',
  });

  // Multiple contaminants state
  const [contaminants, setContaminants] = useState<ContaminantEntry[]>([
    { id: '1', type: 'benzene', concentration_mg_l: '', mcl: 5 }
  ]);

  // Plume perimeter coordinates state (for manual input to enhance extracted boundaries)
  const [plumePerimeterCoords, setPlumePerimeterCoords] = useState<string>('');
  const [parsedPlumePerimeter, setParsedPlumePerimeter] = useState<[number, number][] | null>(null);
  const [plumePerimeterError, setPlumePerimeterError] = useState<string | null>(null);

  // Parse plume perimeter coordinates from text input
  const parsePlumePerimeterCoords = (text: string) => {
    if (!text.trim()) {
      setParsedPlumePerimeter(null);
      setPlumePerimeterError(null);
      return;
    }
    try {
      // Support multiple formats:
      // 1. JSON array: [[lat, lon], [lat, lon], ...]
      // 2. CSV format: lat,lon (one per line)
      // 3. Space-separated: lat lon (one per line)
      let coords: [number, number][] = [];

      const trimmed = text.trim();
      if (trimmed.startsWith('[')) {
        // JSON array format
        coords = JSON.parse(trimmed);
      } else {
        // Line-by-line format
        const lines = trimmed.split('\n').filter(l => l.trim());
        coords = lines.map(line => {
          const parts = line.trim().split(/[,\s]+/).map(p => parseFloat(p.trim()));
          if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
            return [parts[0], parts[1]] as [number, number];
          }
          throw new Error(`Invalid coordinate: ${line}`);
        });
      }

      // Validate coordinates
      if (coords.length < 3) {
        throw new Error('At least 3 coordinates required for a polygon');
      }
      for (const [lat, lon] of coords) {
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
          throw new Error(`Invalid lat/lon: ${lat}, ${lon}`);
        }
      }

      setParsedPlumePerimeter(coords);
      setPlumePerimeterError(null);
    } catch (err) {
      setParsedPlumePerimeter(null);
      setPlumePerimeterError(err instanceof Error ? err.message : 'Invalid coordinates');
    }
  };

  // Helper to generate unique ID
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Add a new contaminant
  const addContaminant = () => {
    const defaultType = CONTAMINANT_OPTIONS[0];
    setContaminants([...contaminants, {
      id: generateId(),
      type: defaultType.value,
      concentration_mg_l: '',
      mcl: defaultType.mcl
    }]);
  };

  // Remove a contaminant
  const removeContaminant = (id: string) => {
    if (contaminants.length > 1) {
      setContaminants(contaminants.filter(c => c.id !== id));
    }
  };

  // Update a contaminant
  const updateContaminant = (id: string, field: keyof ContaminantEntry, value: string | number) => {
    setContaminants(contaminants.map(c => {
      if (c.id !== id) return c;
      if (field === 'type') {
        const option = CONTAMINANT_OPTIONS.find(o => o.value === value);
        return { ...c, type: value as string, mcl: option?.mcl || 100 };
      }
      return { ...c, [field]: value };
    }));
  };

  // Remediation options
  const REMEDIATION_OPTIONS = [
    { value: 'none', label: 'No Active Remediation (MNA)' },
    { value: 'mpe', label: 'Multi-Phase Extraction (MPE)' },
    { value: 'skimming', label: 'LNAPL Skimming' },
    { value: 'sve', label: 'Soil Vapor Extraction (SVE)' },
    { value: 'isco', label: 'In-Situ Chemical Oxidation (ISCO)' },
    { value: 'surfactant', label: 'Surfactant-Enhanced Recovery' },
    { value: 'biostimulation', label: 'Enhanced Biostimulation' },
    { value: 'as_sve', label: 'Air Sparging + SVE' },
  ];

  const [wells, setWells] = useState<WellFormData[]>([]);
  const [autoDetectWells, setAutoDetectWells] = useState(true);
  const [projectionMonths, setProjectionMonths] = useState(24);

  // Coordinate format state
  const [coordFormat, setCoordFormat] = useState<CoordinateFormat>('decimal');
  const [latDms, setLatDms] = useState({ degrees: '', minutes: '', seconds: '', direction: 'N' as 'N' | 'S' });
  const [lonDms, setLonDms] = useState({ degrees: '', minutes: '', seconds: '', direction: 'W' as 'E' | 'W' });

  // Check if EnviroData is configured on mount
  useEffect(() => {
    const checkEnvirodataConfig = async () => {
      try {
        const config = await envirodataApi.getConfig();
        setEnvirodataConfig(config);
      } catch (err) {
        // EnviroData not configured
      }
      setEnvirodataChecked(true);
    };
    checkEnvirodataConfig();
  }, []);

  // Check if AI document extraction is available
  useEffect(() => {
    const checkAiAvailability = async () => {
      try {
        const status = await aiApi.getStatus();
        setAiAvailable(status.available);
      } catch (err) {
        // AI features not available
        setAiAvailable(false);
      }
    };
    checkAiAvailability();
  }, []);

  // Process uploaded file for AI extraction (shared by input and drag-drop)
  const processUploadedFile = async (file: File) => {
    setIsExtractingDocument(true);
    setDocumentError(null);
    setExtractedData(null);

    const filename = file.name.toLowerCase();
    const isExcel = filename.endsWith('.xlsx') || filename.endsWith('.xls') || filename.endsWith('.csv');

    try {
      // Route Excel/CSV files to dedicated spreadsheet extraction endpoint
      if (isExcel) {
        let excelResult;
        try {
          excelResult = await aiApi.extractFromExcel(file);
        } catch (apiError: unknown) {
          // Handle specific API errors
          const err = apiError as { response?: { status?: number; data?: { detail?: string } }; message?: string };
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail || err?.message || 'Unknown error';

          if (status === 500) {
            throw new Error(`Server error processing Excel file. The backend may be missing required dependencies (pandas, openpyxl). Details: ${detail}`);
          } else if (status === 503) {
            throw new Error('AI extraction service unavailable. Please configure the GEMINI_API_KEY or try again later.');
          } else {
            throw new Error(`Failed to extract data from Excel: ${detail}`);
          }
        }

        if (!excelResult) {
          throw new Error('No data extracted from Excel file');
        }

        // Apply Excel extraction to form
        if (excelResult.site_name) {
          setFormData(prev => ({ ...prev, site_name: excelResult.site_name || '' }));
        }
        if (excelResult.latitude !== null && excelResult.latitude !== undefined) {
          setFormData(prev => ({ ...prev, latitude: excelResult.latitude!.toString() }));
        }
        if (excelResult.longitude !== null && excelResult.longitude !== undefined) {
          setFormData(prev => ({ ...prev, longitude: excelResult.longitude!.toString() }));
        }

        // PSH/LNAPL data - critical for POLYGON
        if (excelResult.psh_present) {
          setFormData(prev => ({
            ...prev,
            psh_present: true,
            psh_thickness_ft: excelResult.psh_thickness_ft?.toString() || prev.psh_thickness_ft,
            psh_area_sqft: excelResult.psh_area_sqft?.toString() || prev.psh_area_sqft,
            psh_volume_gallons: excelResult.psh_volume_gallons?.toString() || prev.psh_volume_gallons,
          }));
        }

        // Plume geometry
        if (excelResult.plume_area_sqft) {
          setFormData(prev => ({ ...prev, plume_area_sqft: excelResult.plume_area_sqft!.toString() }));
        }
        if (excelResult.plume_length_ft) {
          setFormData(prev => ({ ...prev, plume_length_ft: excelResult.plume_length_ft!.toString() }));
        }
        if (excelResult.plume_width_ft) {
          setFormData(prev => ({ ...prev, plume_width_ft: excelResult.plume_width_ft!.toString() }));
        }

        // Hydrogeology
        if (excelResult.hydraulic_conductivity_ft_day) {
          setFormData(prev => ({ ...prev, aquifer_k_ft_day: excelResult.hydraulic_conductivity_ft_day!.toString() }));
        }
        if (excelResult.porosity) {
          setFormData(prev => ({ ...prev, aquifer_porosity: excelResult.porosity!.toString() }));
        }
        if (excelResult.hydraulic_gradient) {
          setFormData(prev => ({ ...prev, hydraulic_gradient: excelResult.hydraulic_gradient!.toString() }));
        }
        if (excelResult.depth_to_water_ft) {
          setFormData(prev => ({ ...prev, depth_to_water_ft: excelResult.depth_to_water_ft!.toString() }));
        }
        if (excelResult.flow_direction_degrees) {
          setFormData(prev => ({ ...prev, flow_direction_degrees: excelResult.flow_direction_degrees!.toString() }));
        }
        if (excelResult.aquifer_name) {
          setFormData(prev => ({ ...prev, aquifer_name: excelResult.aquifer_name || '' }));
        }
        if (excelResult.aquifer_thickness_ft) {
          setFormData(prev => ({ ...prev, aquifer_thickness_ft: excelResult.aquifer_thickness_ft!.toString() }));
        }
        if (excelResult.transmissivity_ft2_day) {
          setFormData(prev => ({ ...prev, transmissivity_ft2_day: excelResult.transmissivity_ft2_day!.toString() }));
        }
        if (excelResult.remediation_type) {
          setFormData(prev => ({ ...prev, remediation_type: excelResult.remediation_type || 'none' }));
        }

        // Contaminants from Excel (backend returns ug/L, convert to mg/L for display)
        if (excelResult.contaminants && excelResult.contaminants.length > 0) {
          const newContaminants: ContaminantEntry[] = excelResult.contaminants.slice(0, 10).map((c, idx) => {
            const contaminantOption = CONTAMINANT_OPTIONS.find(o => o.value === c.name);
            // Backend returns concentration_ug_l, convert to mg/L for form display
            const concMgL = c.concentration_ug_l ? (c.concentration_ug_l / 1000) : 0;
            return {
              id: Date.now().toString() + idx,
              type: c.name || 'benzene',
              concentration_mg_l: concMgL > 0 ? concMgL.toString() : '',
              mcl: contaminantOption?.mcl || c.mcl || 5,
            };
          });
          setContaminants(newContaminants);
        }

        // Wells from Excel
        if (excelResult.wells && excelResult.wells.length > 0) {
          const newWells: WellFormData[] = excelResult.wells.map(w => ({
            well_id: w.well_id,
            latitude: w.latitude?.toString() || '',
            longitude: w.longitude?.toString() || '',
            distance_from_source_ft: '',
            direction_from_source_degrees: '',
          }));
          setWells(newWells);
        }

        setAutoPopulated(true);
        setDataSources(['excel_upload']);

        // After Excel extraction, trigger hydrogeology fetch if lat/lon were extracted
        if (excelResult.latitude && excelResult.longitude) {
          // Reset fetch lock to allow new fetch
          fetchInProgressRef.current = false;
          // Trigger fetch after a short delay to let state update
          setTimeout(() => {
            fetchSiteData(excelResult.latitude!, excelResult.longitude!);
          }, 500);
        }
      } else {
        // For PDF/other documents, use original extraction logic
        let docType: DocumentType = 'lab_report';
        if (filename.includes('remediation') || filename.includes('progress')) {
          docType = 'remediation_report';
        } else if (filename.includes('assessment') || filename.includes('phase')) {
          docType = 'site_assessment';
        } else if (filename.includes('monitoring') || filename.includes('quarterly')) {
          docType = 'monitoring_report';
        } else if (filename.includes('well') && filename.includes('log')) {
          docType = 'well_log';
        }

        let result;
        try {
          result = await aiApi.extractFromDocument(file, docType);
        } catch (apiError: unknown) {
          // Handle specific API errors
          const err = apiError as { response?: { status?: number; data?: { detail?: string } }; message?: string };
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail || err?.message || 'Unknown error';

          // Check for rate limit errors
          if (detail.includes('rate_limit') || detail.includes('rate limit') || detail.includes('429')) {
            throw new Error('API rate limit reached. Please wait a minute and try again, or upload a smaller document.');
          }
          // Check for PDF page limit or token limit errors
          if (detail.includes('PDF pages') || detail.includes('too long') || detail.includes('tokens') || detail.includes('prompt is too long')) {
            throw new Error('Document too large for AI processing. Dense PDFs are limited to ~50 pages. Try uploading specific sections only.');
          } else if (status === 500 && detail.includes('prompt is too long')) {
            throw new Error('Document too large for AI processing. Dense PDFs are limited to ~50 pages. Try uploading specific sections only.');
          } else if (status === 500) {
            throw new Error(`Server error processing document. Details: ${detail}`);
          } else if (status === 503) {
            throw new Error('AI extraction service unavailable. Please ensure GEMINI_API_KEY is configured on the backend.');
          } else {
            throw new Error(`Failed to extract data from document: ${detail}`);
          }
        }

        if (!result) {
          throw new Error('No data extracted from document');
        }
        setExtractedData(result);

        // Apply extracted data to form
        if (result.site_name) {
          setFormData(prev => ({ ...prev, site_name: result.site_name || '' }));
        }
        if (result.latitude) {
          setFormData(prev => ({ ...prev, latitude: result.latitude!.toString() }));
        }
        if (result.longitude) {
          setFormData(prev => ({ ...prev, longitude: result.longitude!.toString() }));
        }
        // NOTE: Hydrogeology data is NOT extracted from documents
        // It comes exclusively from USGS/DataHub toolkit to avoid conflicting data
        if (result.hydrogeology) {
        }
        if (result.plume_data) {
          if (result.plume_data.area_sqft) {
            setFormData(prev => ({ ...prev, plume_area_sqft: result.plume_data!.area_sqft!.toString() }));
          }
          if (result.plume_data.length_ft) {
            setFormData(prev => ({ ...prev, plume_length_ft: result.plume_data!.length_ft!.toString() }));
          }
          if (result.plume_data.width_ft) {
            setFormData(prev => ({ ...prev, plume_width_ft: result.plume_data!.width_ft!.toString() }));
          }
        }
        // PSH/LNAPL data from document
        if (result.psh_present !== undefined) {
          setFormData(prev => ({
            ...prev,
            psh_present: result.psh_present || false,
            psh_thickness_ft: result.psh_thickness_ft?.toString() || prev.psh_thickness_ft,
            psh_area_sqft: result.psh_area_sqft?.toString() || prev.psh_area_sqft,
          }));
        }
        if (result.contaminants && result.contaminants.length > 0) {

          // Deduplicate contaminants by name - keep only unique contaminant types
          const uniqueContaminants = result.contaminants.reduce((acc: typeof result.contaminants, c) => {
            const existing = acc.find(e => e.name === c.name);
            if (!existing) {
              acc.push(c);
            } else if (c.concentration_ug_l && (!existing.concentration_ug_l || c.concentration_ug_l > existing.concentration_ug_l)) {
              // If duplicate, keep the one with higher concentration
              const idx = acc.indexOf(existing);
              acc[idx] = c;
            }
            return acc;
          }, []);


          const newContaminants: ContaminantEntry[] = uniqueContaminants.slice(0, 10).map((c, idx) => {
            const contaminantOption = CONTAMINANT_OPTIONS.find(o => o.value === c.name);
            // Backend returns concentration_ug_l, convert to mg/L for form display
            const concMgL = c.concentration_ug_l ? (c.concentration_ug_l / 1000) : 0;
            return {
              id: Date.now().toString() + '-' + idx,
              type: c.name || 'benzene',
              concentration_mg_l: concMgL > 0 ? concMgL.toString() : '',
              mcl: contaminantOption?.mcl || c.mcl || 5,
            };
          });
          setContaminants(newContaminants);
        }

        // Extract wells from document and add to autoDetectedWells
        if (result.well_inventory && result.well_inventory.length > 0) {
          const extractedWells = result.well_inventory.map((w: WellInventoryItem) => ({
            well_id: w.well_id || 'Unknown',
            latitude: w.lat,
            longitude: w.lon,
            well_type: w.well_type || 'monitoring',
            status: w.status || 'active',
            depth_ft: w.depth_ft,
            pump_rate_gpm: w.pump_rate_gpm,
            source: 'document_extraction' as const,
          }));
          setAutoDetectedWells(prev => {
            // Merge with existing wells, avoiding duplicates by well_id
            const existingIds = new Set(prev.map(w => w.well_id));
            const newWells = extractedWells.filter(w => !existingIds.has(w.well_id));
            const merged = [...prev, ...newWells];
            autoDetectedWellsRef.current = merged;
            return merged;
          });
        }

        setAutoPopulated(true);
        setDataSources(['document_upload']);

        // After document extraction, trigger hydrogeology fetch if lat/lon were extracted
        if (result.latitude && result.longitude) {
          // Mark that document extraction triggered the fetch (so useEffect skips)
          documentFetchTriggeredRef.current = true;
          // Reset fetch lock to allow new fetch
          fetchInProgressRef.current = false;
          // Trigger fetch after a short delay to let state updates complete
          setTimeout(() => {
            fetchSiteData(result.latitude!, result.longitude!);
            // Reset the flag after fetch completes (with delay for async)
            setTimeout(() => {
              documentFetchTriggeredRef.current = false;
            }, 5000);
          }, 500);
        }
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Document extraction failed:', err);
      setDocumentError(err instanceof Error ? err.message : 'Failed to extract data from document');
    } finally {
      setIsExtractingDocument(false);
    }
  };

  // Handle document upload from file input
  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processUploadedFile(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the drop zone entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'image/png',
        'image/jpeg',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      const validExtensions = ['.pdf', '.xlsx', '.xls', '.csv', '.png', '.jpg', '.jpeg', '.txt', '.doc', '.docx'];

      const isValidType = validTypes.includes(file.type) ||
        validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

      if (isValidType) {
        await processUploadedFile(file);
      } else {
        setDocumentError('Invalid file type. Please upload Excel (.xlsx, .xls), CSV, PDF, Image, or Text files.');
      }
    }
  };

  // Track if a fetch is already in progress to prevent concurrent calls
  const fetchInProgressRef = React.useRef(false);
  // Track if document extraction already triggered a fetch (to skip useEffect duplicate)
  const documentFetchTriggeredRef = React.useRef(false);

  // Debounced auto-fetch when lat/lon change
  // Flow: 1) Gemini AI/EnviroData/GIS for plume data, 2) USGS always fills missing hydrogeology
  const fetchSiteData = useCallback(async (lat: number, lon: number) => {

    if (isNaN(lat) || isNaN(lon)) {
      return;
    }
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      return;
    }

    // Prevent concurrent fetches
    if (fetchInProgressRef.current) {
      return;
    }

    fetchInProgressRef.current = true;
    setIsFetchingSiteData(true);
    setError(null);

    // Track what data we've collected
    let hasClientData = false;
    let dataSourcesToShow: string[] = [];

    // Temporary form data to merge
    let mergedFormData: Partial<typeof formData> = {};
    let mergedWells: HydroData['existing_wells'] = [];
    let mergedContaminants: ContaminantEntry[] = [];

    try {
      // STEP 1: Try Gemini AI endpoint for intelligent data fetching (EnviroData/GIS)
      try {
        const response = await aiApi.fetchSiteData({
          latitude: lat,
          longitude: lon,
          products: ['polygon'],
        });

        if (response.found && response.polygon_data) {
          hasClientData = true;
          dataSourcesToShow.push(response.data_source || 'gemini_ai');

          const polygonData = response.polygon_data;

          // Collect plume/site data from Gemini AI
          mergedFormData = {
            ...mergedFormData,
            site_name: polygonData.site_name || undefined,
            contaminant_type: polygonData.contaminant_type || undefined,
            plume_area_sqft: polygonData.plume_area_sqft?.toString() || undefined,
            plume_length_ft: polygonData.plume_length_ft?.toString() || undefined,
            plume_width_ft: polygonData.plume_width_ft?.toString() || undefined,
            psh_present: polygonData.psh_present || undefined,
            psh_thickness_ft: polygonData.psh_thickness_ft?.toString() || undefined,
            psh_area_sqft: polygonData.psh_area_sqft?.toString() || undefined,
            remediation_type: polygonData.remediation_type || undefined,
            // NOTE: Hydrogeology is NOT taken from Gemini AI / client data
            // It comes exclusively from USGS/DataHub toolkit to ensure accuracy
          };

          // Collect contaminants from Gemini AI
          if (polygonData.contaminant_concentration_mg_l) {
            const contaminantType = polygonData.contaminant_type || 'benzene';
            const contaminantOption = CONTAMINANT_OPTIONS.find(o => o.value === contaminantType);
            mergedContaminants = [{
              id: Date.now().toString(),
              type: contaminantType,
              concentration_mg_l: polygonData.contaminant_concentration_mg_l.toString(),  // Already in mg/L
              mcl: contaminantOption?.mcl || 5,
            }];
          }

          // Collect wells from Gemini AI
          if (response.monitoring_wells && response.monitoring_wells.length > 0) {
            mergedWells = response.monitoring_wells.map(well => ({
              well_id: well.well_id,
              latitude: well.latitude,
              longitude: well.longitude,
              distance_ft: well.distance_from_source_ft || 0,
              well_depth_ft: well.depth_ft,
              source_database: 'Gemini AI',
              source: response.data_source || 'AI Analysis',
            })) as HydroData['existing_wells'];
          }
        }
      } catch (err) {
        // Gemini AI fetch failed, trying legacy EnviroData
      }

      // STEP 2: ALWAYS fetch from ALL toolkits via DataHub to fill in missing data
      try {
        const datahubData = await datahubApi.enrich(lat, lon, 'POLYGON');

        if (datahubData) {
          const autoFill = datahubData.auto_fill || {};

          // Add toolkit sources
          if (datahubData.toolkits_called) {
            dataSourcesToShow.push(...datahubData.toolkits_called.filter((t: string) => !dataSourcesToShow.includes(t)));
          }

          // Fill in ALL missing fields from ALL toolkits via auto_fill
          mergedFormData = {
            ...mergedFormData,
            // Hydrogeology - ALWAYS from USGS/DataHub toolkit (not from documents)
            aquifer_k_ft_day: autoFill.hydraulicConductivityFtDay?.toFixed(2) || undefined,
            aquifer_porosity: autoFill.porosity?.toFixed(3) || undefined,
            hydraulic_gradient: autoFill.hydraulicGradient?.toFixed(5) || undefined,
            flow_direction_degrees: autoFill.groundwaterFlowDirectionDeg?.toFixed(0) || undefined,
            aquifer_name: autoFill.aquiferName || datahubData.groundwater?.aquifer_name || undefined,
            depth_to_water_ft: autoFill.depthToWaterFt?.toFixed(1) || datahubData.groundwater?.depth_to_water_ft?.toFixed(1) || undefined,
            soil_type: autoFill.soilType || datahubData.soil?.soil_type || undefined,
            organic_carbon_foc: autoFill.organicCarbonFraction?.toFixed(4) || datahubData.soil?.organic_carbon_fraction?.toFixed(4) || undefined,
            // Additional fields from USGS/DataHub
            elevation_ft: autoFill.elevationFt?.toFixed(1) || undefined,
            gw_velocity_ft_day: autoFill.groundwaterVelocityFtDay?.toFixed(4) || undefined,
            aquifer_thickness_ft: autoFill.aquiferThicknessFt?.toFixed(1) || undefined,
            transmissivity_ft2_day: autoFill.transmissivityFt2Day?.toFixed(2) || undefined,
            // Plume data from toolkits
            plume_area_sqft: mergedFormData.plume_area_sqft || autoFill.plumeAreaSqft?.toString() || datahubData.plume?.plume_area_sqft?.toString() || undefined,
            plume_length_ft: mergedFormData.plume_length_ft || autoFill.plumeLengthFt?.toString() || datahubData.plume?.plume_length_ft?.toString() || undefined,
            plume_width_ft: mergedFormData.plume_width_ft || autoFill.plumeWidthFt?.toString() || datahubData.plume?.plume_width_ft?.toString() || undefined,
            // PSH data from toolkits
            // IMPORTANT: Only accept explicit TRUE from DataHub, not FALSE which would override document extraction
            psh_present: mergedFormData.psh_present !== undefined
              ? mergedFormData.psh_present
              : (autoFill.pshPresent === true ? true : undefined),
            psh_thickness_ft: mergedFormData.psh_thickness_ft || autoFill.pshThicknessFt?.toString() || undefined,
            psh_area_sqft: mergedFormData.psh_area_sqft || autoFill.pshAreaSqft?.toString() || undefined,
          };

          // Contaminants from toolkit data
          if (mergedContaminants.length === 0 && (autoFill.maxConcentrationMgL || datahubData.plume?.max_concentration_ug_l)) {
            // autoFill.maxConcentrationMgL is already in mg/L, plume data is in ug/L so divide by 1000
            const concMgL = autoFill.maxConcentrationMgL || (datahubData.plume?.max_concentration_ug_l ? datahubData.plume.max_concentration_ug_l / 1000 : null);
            const contaminantType = autoFill.contaminantType || datahubData.plume?.contaminant_type || 'benzene';
            if (concMgL) {
              const contaminantOption = CONTAMINANT_OPTIONS.find(o => o.value === contaminantType);
              mergedContaminants = [{
                id: Date.now().toString(),
                type: contaminantType,
                concentration_mg_l: concMgL.toString(),  // Store in mg/L
                mcl: contaminantOption?.mcl || 5,
              }];
            }
          }

          // Debug: Log all possible well sources

          // Wells from toolkits - check multiple paths
          if (mergedWells.length === 0 && autoFill.monitoringWells && autoFill.monitoringWells.length > 0) {
            mergedWells = autoFill.monitoringWells.map((well: WellInfo) => ({
              well_id: well.well_id || `MW-${Math.random().toString(36).substr(2, 4)}`,
              latitude: well.latitude,
              longitude: well.longitude,
              distance_ft: well.distance_from_source_ft || 0,
              well_depth_ft: well.depth_ft,
              source_database: 'DataHub',
              source: 'All Toolkits',
            })) as HydroData['existing_wells'];
          }

          // Also check wells in the separate wells response
          if (mergedWells.length === 0 && datahubData.wells?.wells && datahubData.wells.wells.length > 0) {
            mergedWells = datahubData.wells.wells.map((well: WellInfo) => ({
              well_id: well.well_id || `MW-${Math.random().toString(36).substr(2, 4)}`,
              latitude: well.latitude,
              longitude: well.longitude,
              distance_ft: well.distance_from_source_ft || 0,
              well_depth_ft: well.depth_ft,
              source_database: well.source || 'DataHub',
              source: well.source || 'All Toolkits',
            })) as HydroData['existing_wells'];
          }

          // Also check direct existing_wells path
          const datahubWithLegacyWells = datahubData as EnrichmentResponse & { existing_wells?: WellInfo[] };
          if (mergedWells.length === 0 && datahubWithLegacyWells.existing_wells && datahubWithLegacyWells.existing_wells.length > 0) {
            mergedWells = datahubWithLegacyWells.existing_wells.map((well: WellInfo) => ({
              well_id: well.well_id || `MW-${Math.random().toString(36).substr(2, 4)}`,
              latitude: well.latitude,
              longitude: well.longitude,
              distance_ft: well.distance_from_source_ft || 0,
              well_depth_ft: well.depth_ft,
              source_database: well.source || 'DataHub',
              source: well.source || 'DataHub',
            })) as HydroData['existing_wells'];
          }


          setHydroData({
            // Use auto_fill.hydraulicConductivityFtDay which is already in ft/day
            // Fallback to soil.hydraulic_conductivity which is also already in ft/day from USGS service
            hydraulic_conductivity_ft_day: autoFill.hydraulicConductivityFtDay || datahubData.soil?.hydraulic_conductivity,
            porosity: datahubData.soil?.porosity,
            soil_type: datahubData.soil?.soil_type,
            aquifer_name: datahubData.groundwater?.aquifer_name,
            depth_to_water_ft: datahubData.groundwater?.depth_to_water_ft,
            hydraulic_gradient: autoFill.hydraulicGradient || datahubData.groundwater?.hydraulic_gradient,
            flow_direction_degrees: datahubData.groundwater?.groundwater_flow_direction_deg,
            existing_wells: mergedWells,
          } as HydroData);

        }
      } catch (err: unknown) {
        const error = err as { message?: string; response?: { data?: unknown } };
        console.error('[POLYGON] DataHub fetch FAILED:', error?.message || err);
        console.error('[POLYGON] Error details:', error?.response?.data || err);
        // Fallback to legacy USGS endpoint
        try {
          const usgsData = await polygonApi.fetchSiteData(lat, lon);
          setHydroData(usgsData);
          dataSourcesToShow.push('usgs_legacy');

          mergedFormData = {
            ...mergedFormData,
            aquifer_k_ft_day: mergedFormData.aquifer_k_ft_day || usgsData.hydraulic_conductivity_ft_day?.toFixed(2) || undefined,
            aquifer_porosity: mergedFormData.aquifer_porosity || usgsData.porosity?.toFixed(3) || undefined,
            hydraulic_gradient: mergedFormData.hydraulic_gradient || usgsData.hydraulic_gradient?.toFixed(5) || undefined,
            flow_direction_degrees: mergedFormData.flow_direction_degrees || usgsData.flow_direction_degrees?.toFixed(0) || undefined,
            aquifer_name: mergedFormData.aquifer_name || usgsData.aquifer_name || usgsData.soil_type || undefined,
            depth_to_water_ft: mergedFormData.depth_to_water_ft || usgsData.depth_to_water_ft?.toFixed(1) || undefined,
            soil_type: mergedFormData.soil_type || usgsData.soil_type || undefined,
          };

          if (mergedWells.length === 0 && usgsData.existing_wells && usgsData.existing_wells.length > 0) {
            mergedWells = usgsData.existing_wells;
          }
        } catch (fallbackErr) {
          // Legacy USGS fetch also failed
        }
      }

      // STEP 3: Apply merged data to form

      // Check if we actually got any data
      const hasData = Object.keys(mergedFormData).filter(k => mergedFormData[k as keyof typeof mergedFormData] !== undefined).length > 0;

      if (hasData) {
        setFormData(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(mergedFormData).filter(([_, v]) => v !== undefined)
          ),
        }));

        if (mergedContaminants.length > 0) {
          setContaminants(mergedContaminants);
        }

        if (mergedWells.length > 0) {
          setAutoDetectedWells(mergedWells);
          autoDetectedWellsRef.current = mergedWells;  // Also update ref to avoid stale closure
        }

        // Set data source info - only if we got data
        setAutoPopulated(true);
        setDataSource(hasClientData ? 'envirodata' : 'usgs');
        setDataSources(dataSourcesToShow);
      } else {
        setError('Unable to auto-populate data. Please enter values manually or check your network connection.');
      }

    } catch (err) {
      console.error('Failed to fetch site data:', err);
    } finally {
      setIsFetchingSiteData(false);
      fetchInProgressRef.current = false;
    }
  }, []);

  // Trigger auto-fetch when coordinates are complete
  useEffect(() => {
    const lat = parseFloat(formData.latitude);
    const lon = parseFloat(formData.longitude);

    if (!isNaN(lat) && !isNaN(lon) && formData.latitude.length >= 5 && formData.longitude.length >= 5) {
      // Skip if document extraction already triggered a fetch
      if (documentFetchTriggeredRef.current) {
        return;
      }

      const timeoutId = setTimeout(() => {
        // Double-check the flag at execution time
        if (documentFetchTriggeredRef.current) {
          return;
        }
        fetchSiteData(lat, lon);
      }, 1000); // 1 second debounce

      return () => clearTimeout(timeoutId);
    } else {
    }
  }, [formData.latitude, formData.longitude, fetchSiteData]);

  // Auto-calculate transmissivity T = K × b when both values are available
  useEffect(() => {
    const k = parseFloat(formData.aquifer_k_ft_day);
    const b = parseFloat(formData.aquifer_thickness_ft);

    if (!isNaN(k) && !isNaN(b) && k > 0 && b > 0) {
      const transmissivity = k * b;
      setFormData(prev => ({
        ...prev,
        transmissivity_ft2_day: transmissivity.toFixed(2)
      }));
    }
  }, [formData.aquifer_k_ft_day, formData.aquifer_thickness_ft]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Reset auto-populated flag if user manually changes hydro fields
    if (['aquifer_k_ft_day', 'aquifer_porosity', 'hydraulic_gradient', 'flow_direction_degrees'].includes(name)) {
      setAutoPopulated(false);
    }
  };

  // Handle coordinate format change
  const handleCoordFormatChange = (format: CoordinateFormat) => {
    setCoordFormat(format);

    if (format === 'dms' && formData.latitude && formData.longitude) {
      // Convert decimal to DMS
      const lat = parseFloat(formData.latitude);
      const lon = parseFloat(formData.longitude);
      if (!isNaN(lat)) {
        const latDmsVal = decimalToDms(lat, true);
        setLatDms({
          degrees: latDmsVal.degrees.toString(),
          minutes: latDmsVal.minutes.toString(),
          seconds: latDmsVal.seconds.toFixed(3),
          direction: latDmsVal.direction as 'N' | 'S'
        });
      }
      if (!isNaN(lon)) {
        const lonDmsVal = decimalToDms(lon, false);
        setLonDms({
          degrees: lonDmsVal.degrees.toString(),
          minutes: lonDmsVal.minutes.toString(),
          seconds: lonDmsVal.seconds.toFixed(3),
          direction: lonDmsVal.direction as 'E' | 'W'
        });
      }
    }
  };

  // Handle DMS input changes
  const handleLatDmsChange = (field: 'degrees' | 'minutes' | 'seconds' | 'direction', value: string) => {
    const newLatDms = { ...latDms, [field]: value };
    setLatDms(newLatDms);

    // Convert to decimal and update formData
    const deg = parseFloat(newLatDms.degrees) || 0;
    const min = parseFloat(newLatDms.minutes) || 0;
    const sec = parseFloat(newLatDms.seconds) || 0;
    if (deg || min || sec) {
      const decimal = dmsToDecimal(deg, min, sec, newLatDms.direction);
      setFormData(prev => ({ ...prev, latitude: decimal.toFixed(6) }));
    }
  };

  const handleLonDmsChange = (field: 'degrees' | 'minutes' | 'seconds' | 'direction', value: string) => {
    const newLonDms = { ...lonDms, [field]: value };
    setLonDms(newLonDms);

    // Convert to decimal and update formData
    const deg = parseFloat(newLonDms.degrees) || 0;
    const min = parseFloat(newLonDms.minutes) || 0;
    const sec = parseFloat(newLonDms.seconds) || 0;
    if (deg || min || sec) {
      const decimal = dmsToDecimal(deg, min, sec, newLonDms.direction);
      setFormData(prev => ({ ...prev, longitude: decimal.toFixed(6) }));
    }
  };

  const addWell = () => {
    setWells(prev => [...prev, {
      well_id: `MW-${prev.length + 1}`,
      latitude: '',
      longitude: '',
      distance_from_source_ft: '',
      direction_from_source_degrees: '',
    }]);
  };

  const removeWell = (index: number) => {
    setWells(prev => prev.filter((_, i) => i !== index));
  };

  const updateWell = (index: number, field: keyof WellFormData, value: string) => {
    setWells(prev => prev.map((well, i) =>
      i === index ? { ...well, [field]: value } : well
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const latitude = parseFloat(formData.latitude);
      const longitude = parseFloat(formData.longitude);
      const plume_area = parseFloat(formData.plume_area_sqft) || 10000;

      // Prepare contaminants array for API (convert mg/L to ug/L for backend)
      const contaminantsData = contaminants.map(c => ({
        type: c.type,
        concentration_ug_l: (parseFloat(c.concentration_mg_l) || 0) * 1000,  // Convert mg/L to ug/L
        mcl: c.mcl
      })).filter(c => c.concentration_ug_l > 0);

      // Use first/primary contaminant for legacy single-contaminant APIs
      const primaryContaminant = contaminantsData[0] || { type: 'benzene', concentration_ug_l: 500, mcl: 5 };

      // Generate a site ID
      const siteId = `SITE-${Date.now().toString(36).toUpperCase()}`;

      // If PSH is present, run the three-plume simulation for each contaminant
      let threePlumeResult = null;
      if (formData.psh_present) {
        try {
          // Calculate PSH area if not provided - use plume area fraction or default
          let pshAreaSqft = parseFloat(formData.psh_area_sqft);
          if (isNaN(pshAreaSqft) || pshAreaSqft <= 0) {
            const plumeArea = parseFloat(formData.plume_area_sqft);
            if (!isNaN(plumeArea) && plumeArea > 0) {
              // PSH source zone is typically 10-20% of total dissolved plume area
              pshAreaSqft = Math.round(plumeArea * 0.15);
            } else {
              // Default to 2500 sqft (roughly 50x50 ft source zone)
              pshAreaSqft = 2500;
            }
          }

          // Run multi-contaminant three-plume simulation
          const threePlumeRequest: Record<string, unknown> = {
            psh_thickness_ft: parseFloat(formData.psh_thickness_ft) || undefined,
            psh_area_sqft: pshAreaSqft,
            psh_volume_gallons: parseFloat(formData.psh_volume_gallons) || undefined,
            psh_saturation_percent: parseFloat(formData.psh_saturation_percent) || undefined,
            // Pass all contaminants for multi-contaminant modeling
            contaminants: contaminantsData,
            // Legacy single field for backwards compatibility
            contaminant_type: primaryContaminant.type,
            hydraulic_conductivity_ft_day: parseFloat(formData.aquifer_k_ft_day) || 10.0,
            porosity: parseFloat(formData.aquifer_porosity) || 0.35,
            hydraulic_gradient: parseFloat(formData.hydraulic_gradient) || 0.01,
            depth_to_water_ft: parseFloat(formData.depth_to_water_ft) || 15.0,
            organic_carbon_foc: parseFloat(formData.organic_carbon_foc) || 0.005,
            remediation_type: formData.remediation_type,
            projection_months: projectionMonths,
            // Flow direction from extracted data or form
            flow_direction_degrees: extractedData?.gradient_data?.average_flow_direction_deg
              ?? (parseFloat(formData.flow_direction_degrees) || 180.0),
          };

          // Add extracted gradient field for physics (if available)
          if (extractedData?.gradient_data) {
            threePlumeRequest.gradient_field = extractedData.gradient_data;
          }

          // Add plume boundaries for seeding - prioritize manual input over extracted
          if (parsedPlumePerimeter && parsedPlumePerimeter.length >= 3) {
            // Use manually entered plume perimeter coordinates
            threePlumeRequest.seed_plume = {
              boundaries: [{
                contaminant: contaminantsData[0]?.type || 'benzene',
                concentration_ug_l: contaminantsData[0]?.concentration_ug_l || 5.0,
                is_mcl_contour: true,
                boundary_coords: parsedPlumePerimeter,
                area_sqft: null,
              }],
              source_location: extractedData?.source_location || null,
            };
          } else if (extractedData?.plume_boundaries && extractedData.plume_boundaries.length > 0) {
            // Use extracted plume boundaries from document
            threePlumeRequest.seed_plume = {
              boundaries: extractedData.plume_boundaries,
              source_location: extractedData.source_location,
            };
          }
          threePlumeResult = await polygonApi.runThreePlumeSimulation(threePlumeRequest);
        } catch (err) {
          console.error('Three-plume simulation failed (continuing with standard analysis):', err);
        }
      }

      // Call combined analysis API (Physics + ML)
      // Pass hydrogeology data from form to override USGS toolkit defaults
      // Also pass existing wells from form auto-detection
      // Use ref to get current wells (avoids stale closure from useState)
      const currentWells = autoDetectedWellsRef.current || autoDetectedWells || [];

      const wellsToPass = currentWells.map(w => ({
        well_id: w.well_id,
        latitude: w.latitude,
        longitude: w.longitude,
        source_database: w.source_database,
        well_depth_ft: w.well_depth_ft,
        distance_ft: w.distance_ft,
      }));

      const result = await polygonApi.runCombinedAnalysis({
        latitude,
        longitude,
        // Pass all contaminants for multi-contaminant modeling
        contaminants: contaminantsData,
        // Legacy single field for backwards compatibility
        contaminant_type: primaryContaminant.type,
        initial_concentration_ug_L: primaryContaminant.concentration_ug_l,
        plume_area_sqft: plume_area,
        projection_months: projectionMonths,
        source_active: true,
        // Hydrogeology parameters from form (populated by USGS toolkit)
        hydraulic_conductivity_ft_day: parseFloat(formData.aquifer_k_ft_day) || undefined,
        porosity: parseFloat(formData.aquifer_porosity) || undefined,
        hydraulic_gradient: parseFloat(formData.hydraulic_gradient) || undefined,
        flow_direction_degrees: parseFloat(formData.flow_direction_degrees) || undefined,
        depth_to_water_ft: parseFloat(formData.depth_to_water_ft) || undefined,
        aquifer_name: formData.aquifer_name || undefined,
        // Site metadata
        site_name: formData.site_name || undefined,
        // Pass existing wells from form auto-detection (avoids re-fetching)
        existing_wells: wellsToPass.length > 0 ? wellsToPass : undefined,
      });

      setSuccess(true);
      setAnalysisResult(result.analysis_id);

      // Debug: Check wells in API response

      // Redirect to site detail with combined analysis results
      const navState = {
        latitude,
        longitude,
        plume_area_sqft: plume_area,
        // Pass all contaminants
        contaminants: contaminantsData,
        // Legacy single fields
        max_concentration_ug_L: primaryContaminant.concentration_ug_l,
        contaminant_type: primaryContaminant.type,
        site_name: formData.site_name || `Site ${result.analysis_id}`,
        // Pass combined analysis results
        combinedAnalysis: result,
        // Pass three-plume results if available
        threePlumeResult: threePlumeResult,
        psh_present: formData.psh_present,
        remediation_type: formData.remediation_type,
        // Pass extracted gradient and plume data for visualization
        extractedGradientData: extractedData?.gradient_data || null,
        // Prioritize manual plume perimeter over extracted boundaries
        extractedPlumeBoundaries: parsedPlumePerimeter && parsedPlumePerimeter.length >= 3
          ? [{
              contaminant: primaryContaminant.type,
              concentration_ug_l: primaryContaminant.concentration_ug_l || 5.0,
              is_mcl_contour: true,
              boundary_coords: parsedPlumePerimeter,
              area_sqft: null,
            }]
          : (extractedData?.plume_boundaries || null),
        extractedSourceLocation: extractedData?.source_location || null,
        // Pass extracted well inventory for recovery/MDPE wells
        extractedWellInventory: extractedData?.well_inventory || null,
        // Flag to indicate manual plume perimeter was provided
        hasManualPlumePerimeter: !!(parsedPlumePerimeter && parsedPlumePerimeter.length >= 3),
      };

      setTimeout(() => {
        navigate(`/polygon/sites/${result.analysis_id}`, { state: navState });
      }, 1500);

    } catch (err) {
      console.error('Analysis failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to analyze site. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="space-y-6">
        <FadeIn>
          <div className="flex flex-col items-center justify-center py-20 bg-mono-900 border border-mono-800 rounded-lg">
            <CheckCircleIcon className="h-20 w-20 text-mono-400 mb-6" />
            <h2 className="text-2xl font-semibold text-mono-100 tracking-wider mb-2 font-orbitron">ANALYSIS COMPLETE</h2>
            <p className="text-mono-500 text-sm text-center max-w-md mb-4">
              Site has been analyzed and added to your portfolio.
              Redirecting to site details...
            </p>
            <div className="animate-pulse text-mono-600 text-xs tracking-widest">
              LOADING RESULTS...
            </div>
          </div>
        </FadeIn>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <FadeIn>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-mono-100 tracking-wider font-orbitron">UPLOAD SITE DATA</h1>
            <p className="text-mono-500 text-sm mt-1">
              {envirodataConfig?.envirodata_configured || envirodataConfig?.gis_configured
                ? 'Enter coordinates to auto-populate from your database'
                : 'Enter site information for POLYGON analysis'}
            </p>
          </div>
          {/* Integration Status */}
          {envirodataChecked && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
              envirodataConfig?.envirodata_configured || envirodataConfig?.gis_configured
                ? 'bg-green-500/10 border-green-500/30'
                : 'bg-mono-800 border-mono-700'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                envirodataConfig?.envirodata_configured || envirodataConfig?.gis_configured ? 'bg-green-400' : 'bg-mono-600'
              }`} />
              <span className={`text-xs tracking-wider ${
                envirodataConfig?.envirodata_configured || envirodataConfig?.gis_configured ? 'text-green-400' : 'text-mono-500'
              }`}>
                {envirodataConfig?.envirodata_configured && envirodataConfig?.gis_configured
                  ? 'ENVIRODATA + GIS CONNECTED'
                  : envirodataConfig?.envirodata_configured
                    ? 'ENVIRODATA CONNECTED'
                    : envirodataConfig?.gis_configured
                      ? 'GIS CONNECTED'
                      : 'NOT CONFIGURED'}
              </span>
            </div>
          )}
        </div>
      </FadeIn>

      {/* Integration Banner - when configured */}
      {(envirodataConfig?.envirodata_configured || envirodataConfig?.gis_configured) && (
        <FadeIn>
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-4">
            <SparklesIcon className="h-6 w-6 text-green-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-green-300 font-semibold text-sm">
                {envirodataConfig?.envirodata_configured && envirodataConfig?.gis_configured
                  ? 'EnviroData + GIS Integration Active'
                  : envirodataConfig?.gis_configured
                    ? 'GIS Database Connected'
                    : 'EnviroData Integration Active'}
              </p>
              <p className="text-green-400/70 text-xs mt-0.5">
                Just enter latitude and longitude - all plume data will be auto-populated from your database.
              </p>
            </div>
            {envirodataConfig?.has_trained_model && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded">
                <span className="text-cyan-400 text-xs">Custom Model: {envirodataConfig.model_version}</span>
              </div>
            )}
          </div>
        </FadeIn>
      )}

      {/* Error Alert */}
      {error && (
        <FadeIn>
          <div className="bg-mono-900 border border-mono-600 rounded-lg p-4 flex items-start gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-mono-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-mono-200 font-semibold">Analysis Failed</h3>
              <p className="text-mono-500 text-sm">{error}</p>
            </div>
          </div>
        </FadeIn>
      )}

      <form onSubmit={handleSubmit}>
        {/* AI Document Extraction Section - Drag & Drop */}
        {(aiAvailable || true) && (
          <FadeIn className="mb-6">
            <div className="bg-gradient-to-r from-purple-900/30 to-cyan-900/30 border border-purple-500/30 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-500/20 rounded">
                  <SparklesIcon className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-purple-300 font-semibold tracking-wider font-orbitron">AI DOCUMENT EXTRACTION</h2>
                  <p className="text-mono-500 text-xs">
                    {envirodataConfig?.envirodata_configured || envirodataConfig?.gis_configured
                      ? 'Optional: Upload additional documents to supplement EnviroData/GIS data'
                      : 'Upload Excel data, lab reports, or site assessments to auto-populate fields (contaminants, PSH/LNAPL, hydrogeology, etc.)'}
                  </p>
                </div>
              </div>

              {/* Drag & Drop Zone */}
              <div
                ref={dropZoneRef}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !isExtractingDocument && fileInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${
                  isDragging
                    ? 'border-purple-400 bg-purple-500/20 scale-[1.02]'
                    : isExtractingDocument
                    ? 'border-mono-600 bg-mono-800/50 cursor-wait'
                    : 'border-purple-500/30 bg-mono-900/50 hover:border-purple-400/50 hover:bg-purple-500/10'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt,.xlsx,.xls,.csv"
                  onChange={handleDocumentUpload}
                  className="hidden"
                  id="document-upload"
                />

                {isExtractingDocument ? (
                  <div className="flex flex-col items-center gap-3">
                    <ArrowPathIcon className="h-10 w-10 text-purple-400 animate-spin" />
                    <span className="text-purple-300 font-medium">Extracting data with Gemini AI...</span>
                    <span className="text-mono-500 text-xs">This may take a moment</span>
                  </div>
                ) : isDragging ? (
                  <div className="flex flex-col items-center gap-3">
                    <DocumentArrowUpIcon className="h-10 w-10 text-purple-400 animate-bounce" />
                    <span className="text-purple-300 font-medium">Drop file here</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <DocumentArrowUpIcon className="h-10 w-10 text-mono-500" />
                    <div>
                      <span className="text-purple-300 font-medium">Drag & drop</span>
                      <span className="text-mono-400"> or </span>
                      <span className="text-purple-300 font-medium underline">click to browse</span>
                    </div>
                    <span className="text-mono-600 text-xs">
                      Excel (.xlsx, .xls), CSV, PDF, Images, Word docs, or Text files up to 50MB
                    </span>
                  </div>
                )}
              </div>

              {documentError && (
                <div className="mt-3 p-3 bg-red-900/30 border border-red-500/30 rounded text-red-400 text-sm flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  {documentError}
                </div>
              )}

              {extractedData && (
                <div className="mt-4 p-4 bg-mono-900/50 border border-purple-500/20 rounded">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircleIcon className="h-4 w-4 text-green-400" />
                    <span className="text-green-400 text-sm font-medium">Data extracted successfully</span>
                    <span className="text-mono-500 text-xs ml-auto">
                      Confidence: {(extractedData.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    {extractedData.site_name && (
                      <div>
                        <span className="text-mono-500 block">Site</span>
                        <span className="text-mono-200">{extractedData.site_name}</span>
                      </div>
                    )}
                    {extractedData.contaminants && (
                      <div>
                        <span className="text-mono-500 block">Contaminants</span>
                        <span className="text-mono-200">{extractedData.contaminants.length} found</span>
                      </div>
                    )}
                    {extractedData.plume_data?.area_sqft && (
                      <div>
                        <span className="text-mono-500 block">Plume Area</span>
                        <span className="text-mono-200">{extractedData.plume_data.area_sqft.toLocaleString()} sqft</span>
                      </div>
                    )}
                    {extractedData.sample_date && (
                      <div>
                        <span className="text-mono-500 block">Sample Date</span>
                        <span className="text-mono-200">{extractedData.sample_date}</span>
                      </div>
                    )}
                  </div>

                  {/* Gradient/Plume Map Extraction Status */}
                  {(extractedData.gradient_data || extractedData.plume_boundaries) && (
                    <div className="mt-4 pt-4 border-t border-purple-500/20">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-purple-400 text-sm font-medium">🗺️ Physics Maps Extracted</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        {extractedData.gradient_data?.contours && extractedData.gradient_data.contours.length > 0 && (
                          <div>
                            <span className="text-mono-500 block">Gradient Contours</span>
                            <span className="text-cyan-400">{extractedData.gradient_data.contours.length} lines</span>
                          </div>
                        )}
                        {extractedData.gradient_data?.flow_arrows && extractedData.gradient_data.flow_arrows.length > 0 && (
                          <div>
                            <span className="text-mono-500 block">Flow Arrows</span>
                            <span className="text-cyan-400">{extractedData.gradient_data.flow_arrows.length} found</span>
                          </div>
                        )}
                        {extractedData.gradient_data?.average_flow_direction_deg !== undefined && (
                          <div>
                            <span className="text-mono-500 block">Flow Direction</span>
                            <span className="text-cyan-400">{extractedData.gradient_data.average_flow_direction_deg}°</span>
                          </div>
                        )}
                        {extractedData.plume_boundaries && extractedData.plume_boundaries.length > 0 && (
                          <div>
                            <span className="text-mono-500 block">Plume Boundaries</span>
                            <span className="text-orange-400">{extractedData.plume_boundaries.length} zones</span>
                          </div>
                        )}
                        {extractedData.source_location && (
                          <div>
                            <span className="text-mono-500 block">Source Location</span>
                            <span className="text-red-400">✓ Identified</span>
                          </div>
                        )}
                      </div>
                      <p className="text-mono-600 text-xs mt-2">
                        These maps will be used to initialize the physics-based plume simulation.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </FadeIn>
        )}

        <StaggeredFadeIn className="space-y-6" staggerDelay={100}>
          {/* Section 1: Basic Info */}
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-mono-800 rounded">
                <MapPinIcon className="h-5 w-5 text-mono-400" />
              </div>
              <div>
                <h2 className="text-mono-100 font-semibold tracking-wider font-orbitron">SITE LOCATION</h2>
                <p className="text-mono-600 text-xs">Required fields</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-mono-400 text-xs tracking-wider mb-2">
                  SITE NAME *
                </label>
                <input
                  type="text"
                  name="site_name"
                  value={formData.site_name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Former Gas Station #127"
                  className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                />
              </div>

              {/* Coordinate Format Selector */}
              <div className="md:col-span-2">
                <label className="block text-mono-400 text-xs tracking-wider mb-2">
                  COORDINATE FORMAT
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleCoordFormatChange('decimal')}
                    className={`flex-1 px-4 py-2 text-xs tracking-wider rounded transition-all ${
                      coordFormat === 'decimal'
                        ? 'bg-cyan-500/20 border border-cyan-500/50 text-cyan-300'
                        : 'bg-mono-950 border border-mono-700 text-mono-400 hover:border-mono-600'
                    }`}
                  >
                    DECIMAL DEGREES
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCoordFormatChange('dms')}
                    className={`flex-1 px-4 py-2 text-xs tracking-wider rounded transition-all ${
                      coordFormat === 'dms'
                        ? 'bg-cyan-500/20 border border-cyan-500/50 text-cyan-300'
                        : 'bg-mono-950 border border-mono-700 text-mono-400 hover:border-mono-600'
                    }`}
                  >
                    DEGREES / MINUTES / SECONDS
                  </button>
                </div>
              </div>

              {/* Decimal Degree Inputs */}
              {coordFormat === 'decimal' && (
                <>
                  <div>
                    <label className="block text-mono-400 text-xs tracking-wider mb-2">
                      LATITUDE *
                    </label>
                    <input
                      type="number"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      required
                      step="0.00001"
                      placeholder="e.g., 29.76328"
                      className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                    />
                  </div>

                  <div>
                    <label className="block text-mono-400 text-xs tracking-wider mb-2">
                      LONGITUDE * <span className="text-mono-600 font-normal">(use negative for West)</span>
                    </label>
                    <input
                      type="number"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      required
                      step="0.00001"
                      placeholder="e.g., -95.36327"
                      className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                    />
                  </div>
                </>
              )}

              {/* DMS Inputs */}
              {coordFormat === 'dms' && (
                <>
                  {/* Latitude DMS */}
                  <div className="md:col-span-2">
                    <label className="block text-mono-400 text-xs tracking-wider mb-2">
                      LATITUDE *
                    </label>
                    <div className="flex gap-2 items-center">
                      <div className="flex-1">
                        <input
                          type="number"
                          value={latDms.degrees}
                          onChange={(e) => handleLatDmsChange('degrees', e.target.value)}
                          placeholder="35"
                          min="0"
                          max="90"
                          className="w-full px-3 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                        />
                        <span className="text-mono-500 text-[10px] mt-1 block text-center">Degrees</span>
                      </div>
                      <span className="text-mono-500 text-lg pb-4">°</span>
                      <div className="flex-1">
                        <input
                          type="number"
                          value={latDms.minutes}
                          onChange={(e) => handleLatDmsChange('minutes', e.target.value)}
                          placeholder="4"
                          min="0"
                          max="59"
                          className="w-full px-3 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                        />
                        <span className="text-mono-500 text-[10px] mt-1 block text-center">Minutes</span>
                      </div>
                      <span className="text-mono-500 text-lg pb-4">'</span>
                      <div className="flex-1">
                        <input
                          type="number"
                          value={latDms.seconds}
                          onChange={(e) => handleLatDmsChange('seconds', e.target.value)}
                          placeholder="27.12"
                          step="0.001"
                          min="0"
                          max="59.999"
                          className="w-full px-3 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                        />
                        <span className="text-mono-500 text-[10px] mt-1 block text-center">Seconds</span>
                      </div>
                      <span className="text-mono-500 text-lg pb-4">"</span>
                      <div className="w-20">
                        <select
                          value={latDms.direction}
                          onChange={(e) => handleLatDmsChange('direction', e.target.value)}
                          className="w-full px-3 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm focus:outline-none focus:border-mono-500"
                        >
                          <option value="N">N</option>
                          <option value="S">S</option>
                        </select>
                        <span className="text-mono-500 text-[10px] mt-1 block text-center">Dir</span>
                      </div>
                    </div>
                  </div>

                  {/* Longitude DMS */}
                  <div className="md:col-span-2">
                    <label className="block text-mono-400 text-xs tracking-wider mb-2">
                      LONGITUDE *
                    </label>
                    <div className="flex gap-2 items-center">
                      <div className="flex-1">
                        <input
                          type="number"
                          value={lonDms.degrees}
                          onChange={(e) => handleLonDmsChange('degrees', e.target.value)}
                          placeholder="100"
                          min="0"
                          max="180"
                          className="w-full px-3 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                        />
                        <span className="text-mono-500 text-[10px] mt-1 block text-center">Degrees</span>
                      </div>
                      <span className="text-mono-500 text-lg pb-4">°</span>
                      <div className="flex-1">
                        <input
                          type="number"
                          value={lonDms.minutes}
                          onChange={(e) => handleLonDmsChange('minutes', e.target.value)}
                          placeholder="47"
                          min="0"
                          max="59"
                          className="w-full px-3 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                        />
                        <span className="text-mono-500 text-[10px] mt-1 block text-center">Minutes</span>
                      </div>
                      <span className="text-mono-500 text-lg pb-4">'</span>
                      <div className="flex-1">
                        <input
                          type="number"
                          value={lonDms.seconds}
                          onChange={(e) => handleLonDmsChange('seconds', e.target.value)}
                          placeholder="27.96"
                          step="0.001"
                          min="0"
                          max="59.999"
                          className="w-full px-3 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                        />
                        <span className="text-mono-500 text-[10px] mt-1 block text-center">Seconds</span>
                      </div>
                      <span className="text-mono-500 text-lg pb-4">"</span>
                      <div className="w-20">
                        <select
                          value={lonDms.direction}
                          onChange={(e) => handleLonDmsChange('direction', e.target.value)}
                          className="w-full px-3 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm focus:outline-none focus:border-mono-500"
                        >
                          <option value="W">W</option>
                          <option value="E">E</option>
                        </select>
                        <span className="text-mono-500 text-[10px] mt-1 block text-center">Dir</span>
                      </div>
                    </div>
                  </div>

                  {/* Show converted decimal values */}
                  {(formData.latitude || formData.longitude) && (
                    <div className="md:col-span-2 text-mono-500 text-xs bg-mono-950 p-3 rounded border border-mono-800">
                      <span className="text-mono-600">Decimal: </span>
                      <span className="text-cyan-400 font-mono">{formData.latitude || '—'}</span>
                      <span className="text-mono-600">, </span>
                      <span className="text-cyan-400 font-mono">{formData.longitude || '—'}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Section 2: Contaminant Info */}
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-mono-800 rounded">
                <BeakerIcon className="h-5 w-5 text-mono-400" />
              </div>
              <div>
                <h2 className="text-mono-100 font-semibold tracking-wider font-orbitron">CONTAMINATION DATA</h2>
                <p className="text-mono-600 text-xs">Contaminant and plume information</p>
              </div>
            </div>

            {/* Multiple Contaminants Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-mono-400 text-xs tracking-wider">
                  CONTAMINANTS * <span className="text-mono-600">(add all detected contaminants)</span>
                </label>
                <button
                  type="button"
                  onClick={addContaminant}
                  className="flex items-center gap-1 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs tracking-wider hover:bg-cyan-500/20 transition-colors rounded"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  ADD CONTAMINANT
                </button>
              </div>

              <div className="space-y-3">
                {contaminants.map((contam, index) => (
                  <div key={contam.id} className="flex items-center gap-3 p-3 bg-mono-950 border border-mono-800 rounded">
                    <span className="text-mono-600 text-xs w-6">{index + 1}.</span>

                    <div className="flex-1">
                      <select
                        value={contam.type}
                        onChange={(e) => updateContaminant(contam.id, 'type', e.target.value)}
                        className="w-full px-3 py-2 bg-mono-900 border border-mono-700 rounded text-mono-200 text-sm focus:outline-none focus:border-mono-500"
                      >
                        <optgroup label="BTEX">
                          {CONTAMINANT_OPTIONS.filter(o => o.group === 'BTEX').map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Petroleum">
                          {CONTAMINANT_OPTIONS.filter(o => o.group === 'Petroleum').map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Chlorinated Solvents">
                          {CONTAMINANT_OPTIONS.filter(o => o.group === 'Chlorinated').map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Other">
                          {CONTAMINANT_OPTIONS.filter(o => o.group === 'Emerging' || o.group === 'Other').map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </optgroup>
                      </select>
                    </div>

                    <div className="w-40">
                      <input
                        type="number"
                        value={contam.concentration_mg_l}
                        onChange={(e) => updateContaminant(contam.id, 'concentration_mg_l', e.target.value)}
                        placeholder="Conc. (mg/L)"
                        step="0.001"
                        className="w-full px-3 py-2 bg-mono-900 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                      />
                    </div>

                    <div className="w-28 text-center">
                      <span className={`text-xs ${
                        parseFloat(contam.concentration_mg_l) * 1000 > contam.mcl
                          ? 'text-red-400'
                          : 'text-mono-500'
                      }`}>
                        RRC Limit: {contam.mcl} µg/L
                      </span>
                    </div>

                    {parseFloat(contam.concentration_mg_l) * 1000 > contam.mcl && (
                      <div className="w-16 text-center">
                        <span className="text-red-400 text-xs font-bold">
                          {(parseFloat(contam.concentration_mg_l) * 1000 / contam.mcl).toFixed(0)}x
                        </span>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => removeContaminant(contam.id)}
                      disabled={contaminants.length === 1}
                      className="p-2 text-mono-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {contaminants.some(c => parseFloat(c.concentration_mg_l) * 1000 > c.mcl) && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded">
                  <div className="flex items-center gap-2 text-red-400 text-xs">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    <span>
                      {contaminants.filter(c => parseFloat(c.concentration_mg_l) * 1000 > c.mcl).length} contaminant(s) exceed MCL limits
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Plume Geometry */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-mono-400 text-xs tracking-wider mb-2">
                  PLUME AREA (sq ft)
                </label>
                <input
                  type="number"
                  name="plume_area_sqft"
                  value={formData.plume_area_sqft}
                  onChange={handleInputChange}
                  placeholder="e.g., 45000"
                  className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                />
              </div>

              <div>
                <label className="block text-mono-400 text-xs tracking-wider mb-2">
                  PLUME LENGTH (ft)
                </label>
                <input
                  type="number"
                  name="plume_length_ft"
                  value={formData.plume_length_ft}
                  onChange={handleInputChange}
                  placeholder="e.g., 300"
                  className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                />
              </div>

              <div>
                <label className="block text-mono-400 text-xs tracking-wider mb-2">
                  PLUME WIDTH (ft)
                </label>
                <input
                  type="number"
                  name="plume_width_ft"
                  value={formData.plume_width_ft}
                  onChange={handleInputChange}
                  placeholder="e.g., 150"
                  className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                />
              </div>
            </div>
          </div>

          {/* Section 2b: PSH/LNAPL Source Zone Data */}
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded transition-colors duration-300 ${formData.psh_present ? 'bg-orange-500/20' : 'bg-mono-800'}`}>
                  <FireIcon className={`h-5 w-5 transition-colors duration-300 ${formData.psh_present ? 'text-orange-400' : 'text-mono-400'}`} />
                </div>
                <div>
                  <h2 className="text-mono-100 font-semibold tracking-wider font-orbitron">PSH/LNAPL SOURCE ZONE</h2>
                  <p className="text-mono-600 text-xs">Phase-separated hydrocarbon (free product) data</p>
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <span className="text-mono-400 text-xs">PSH PRESENT</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    name="psh_present"
                    checked={formData.psh_present}
                    onChange={(e) => setFormData(prev => ({ ...prev, psh_present: e.target.checked }))}
                    className="sr-only"
                  />
                  <div className={`w-12 h-6 rounded-full transition-colors ${formData.psh_present ? 'bg-orange-500' : 'bg-mono-700'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${formData.psh_present ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`}></div>
                  </div>
                </div>
              </label>
            </div>

            {formData.psh_present && (
              <>
                <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <p className="text-orange-300 text-xs">
                    <strong>Three-Plume Model Active:</strong> When PSH is present, POLYGON will model three coupled plumes:
                  </p>
                  <ul className="text-orange-400/80 text-xs mt-2 ml-4 list-disc space-y-1">
                    <li><strong>PSH/LNAPL:</strong> Source zone depletion over time</li>
                    <li><strong>Dissolved Plume:</strong> Groundwater contamination from dissolution</li>
                    <li><strong>Vapor Plume:</strong> Vadose zone contamination from volatilization</li>
                  </ul>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-mono-400 text-xs tracking-wider mb-2">
                      LNAPL THICKNESS (ft) <span className="text-mono-600 font-normal">apparent in wells</span>
                    </label>
                    <input
                      type="number"
                      name="psh_thickness_ft"
                      value={formData.psh_thickness_ft}
                      onChange={handleInputChange}
                      step="0.01"
                      placeholder="e.g., 0.5"
                      className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                    />
                    <p className="text-mono-600 text-[10px] mt-1">Apparent thickness is typically 2-10x actual</p>
                  </div>

                  <div>
                    <label className="block text-mono-400 text-xs tracking-wider mb-2">
                      PSH AREA (sq ft) <span className="text-mono-600 font-normal">estimated footprint</span>
                    </label>
                    <input
                      type="number"
                      name="psh_area_sqft"
                      value={formData.psh_area_sqft}
                      onChange={handleInputChange}
                      placeholder="e.g., 5000"
                      className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                    />
                  </div>

                  <div>
                    <label className="block text-mono-400 text-xs tracking-wider mb-2">
                      ESTIMATED VOLUME (gallons) <span className="text-mono-600 font-normal">optional</span>
                    </label>
                    <input
                      type="number"
                      name="psh_volume_gallons"
                      value={formData.psh_volume_gallons}
                      onChange={handleInputChange}
                      placeholder="e.g., 500"
                      className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                    />
                  </div>

                  <div>
                    <label className="block text-mono-400 text-xs tracking-wider mb-2">
                      SOIL SATURATION (%) <span className="text-mono-600 font-normal">optional</span>
                    </label>
                    <input
                      type="number"
                      name="psh_saturation_percent"
                      value={formData.psh_saturation_percent}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      placeholder="e.g., 15"
                      className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500"
                    />
                  </div>
                </div>

                {/* Remediation Selection */}
                <div className="pt-6 border-t border-mono-800">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    ACTIVE REMEDIATION
                  </label>
                  <select
                    name="remediation_type"
                    value={formData.remediation_type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm focus:outline-none focus:border-mono-500"
                  >
                    {REMEDIATION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-mono-600 text-[10px] mt-2">
                    Select the primary remediation technology being applied to model its effect on PSH depletion and plume evolution.
                  </p>
                </div>
              </>
            )}

            {!formData.psh_present && (
              <div className="text-center py-8 text-mono-600 text-sm">
                <p>No PSH/LNAPL present at site.</p>
                <p className="text-mono-700 text-xs mt-1">Toggle on if free product is present in monitoring wells.</p>
              </div>
            )}
          </div>

          {/* Section 2.5: Plume Perimeter Coordinates (Optional Enhancement) */}
          <div className={`bg-mono-900 border rounded-lg p-6 transition-all duration-500 ${parsedPlumePerimeter ? 'border-cyan-500/30' : 'border-mono-800'}`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded transition-colors duration-300 ${parsedPlumePerimeter ? 'bg-cyan-500/20' : 'bg-mono-800'}`}>
                  <MapPinIcon className={`h-5 w-5 transition-colors duration-300 ${parsedPlumePerimeter ? 'text-cyan-400' : 'text-mono-400'}`} />
                </div>
                <div>
                  <h2 className="text-mono-100 font-semibold tracking-wider font-orbitron">PLUME PERIMETER COORDINATES</h2>
                  <p className="text-mono-600 text-xs">
                    Optional: Provide plume boundary coordinates to enhance extracted geometry
                  </p>
                </div>
              </div>
              {parsedPlumePerimeter && (
                <div className="flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <CheckCircleIcon className="h-4 w-4 text-cyan-400" />
                  <span className="text-cyan-400 text-xs tracking-wider font-semibold">{parsedPlumePerimeter.length} VERTICES</span>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-mono-950/50 border border-mono-800 rounded-lg">
                <p className="text-mono-400 text-xs mb-3">
                  Enter plume boundary coordinates as lat/lon pairs. Supports multiple formats:
                </p>
                <ul className="text-mono-500 text-[10px] space-y-1 ml-4 list-disc">
                  <li>JSON array: [[35.4248, -100.7295], [35.4235, -100.7380], ...]</li>
                  <li>CSV per line: 35.4248, -100.7295</li>
                  <li>Space-separated per line: 35.4248 -100.7295</li>
                </ul>
              </div>

              <div>
                <label className="block text-mono-400 text-xs tracking-wider mb-2">
                  BOUNDARY COORDINATES (lat, lon)
                </label>
                <textarea
                  value={plumePerimeterCoords}
                  onChange={(e) => {
                    setPlumePerimeterCoords(e.target.value);
                    parsePlumePerimeterCoords(e.target.value);
                  }}
                  placeholder="Enter coordinates here...&#10;35.4248, -100.7295&#10;35.4235, -100.7380&#10;35.4260, -100.7350&#10;..."
                  rows={6}
                  className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-500 font-mono"
                />
                {plumePerimeterError && (
                  <p className="text-red-400 text-xs mt-2">{plumePerimeterError}</p>
                )}
              </div>

              {parsedPlumePerimeter && parsedPlumePerimeter.length > 0 && (
                <div className="p-4 bg-cyan-500/5 border border-cyan-500/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-cyan-400 text-xs font-semibold tracking-wider">PREVIEW</span>
                    <span className="text-mono-500 text-xs">{parsedPlumePerimeter.length} vertices</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-mono-400 text-xs max-h-32 overflow-y-auto">
                    {parsedPlumePerimeter.slice(0, 10).map((coord, idx) => (
                      <div key={idx} className="font-mono">
                        {idx + 1}. [{coord[0].toFixed(6)}, {coord[1].toFixed(6)}]
                      </div>
                    ))}
                    {parsedPlumePerimeter.length > 10 && (
                      <div className="text-mono-600">... +{parsedPlumePerimeter.length - 10} more</div>
                    )}
                  </div>
                </div>
              )}

              {/* Show extracted boundaries from document if available */}
              {extractedData?.plume_boundaries && extractedData.plume_boundaries.length > 0 && (
                <div className="p-4 bg-mono-950/50 border border-mono-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-400" />
                    <span className="text-green-400 text-xs font-semibold tracking-wider">EXTRACTED FROM DOCUMENT</span>
                  </div>
                  <p className="text-mono-500 text-xs">
                    {extractedData.plume_boundaries.length} boundary zone(s) extracted with{' '}
                    {extractedData.plume_boundaries.reduce((sum, b) => sum + (b.boundary_coords?.length || 0), 0)} total vertices.
                    Your manual input will override extracted coordinates.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Section 3: Hydrogeology & Site Data (Auto-populated) */}
          <div className={`bg-mono-900 border rounded-lg p-6 transition-all duration-500 ${autoPopulated ? 'border-green-500/30' : 'border-mono-800'}`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded transition-colors duration-300 ${autoPopulated ? 'bg-green-500/20' : 'bg-mono-800'}`}>
                  <CubeIcon className={`h-5 w-5 transition-colors duration-300 ${autoPopulated ? 'text-green-400' : 'text-mono-400'}`} />
                </div>
                <div>
                  <h2 className="text-mono-100 font-semibold tracking-wider font-orbitron">HYDROGEOLOGY & SITE DATA</h2>
                  <p className="text-mono-600 text-xs">
                    {isFetchingSiteData
                      ? envirodataConfig?.envirodata_configured || envirodataConfig?.gis_configured
                        ? 'Fetching from your database...'
                        : 'Fetching from USGS, EPA, TCEQ, TWDB APIs...'
                      : autoPopulated
                        ? dataSource === 'envirodata'
                          ? 'Auto-populated from your database'
                          : 'Auto-populated from national databases'
                        : 'Enter lat/long above to auto-populate these fields'}
                  </p>
                </div>
              </div>
              {isFetchingSiteData && (
                <div className="flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg animate-pulse">
                  <ArrowPathIcon className="h-4 w-4 text-cyan-400 animate-spin" />
                  <span className="text-cyan-400 text-xs tracking-wider font-semibold">FETCHING DATA FROM USGS, EPA, STATE DATABASES...</span>
                </div>
              )}
              {autoPopulated && !isFetchingSiteData && (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <SparklesIcon className="h-4 w-4 text-green-400" />
                  <span className="text-green-400 text-xs tracking-wider font-semibold">
                    {dataSource === 'envirodata'
                      ? dataSources.length > 0
                        ? `FROM ${dataSources.map(s => s.toUpperCase()).join(' + ')}`
                        : 'FROM DATABASE'
                      : 'FROM USGS'}
                  </span>
                </div>
              )}
            </div>

            {/* Data sources banner */}
            {((dataSource === 'envirodata' && dataSources.length > 0) || (hydroData?.data_sources && hydroData.data_sources.length > 0)) && (
              <div className="mb-6 flex items-center gap-2 text-xs">
                <InformationCircleIcon className="h-4 w-4 text-cyan-400" />
                <span className="text-mono-500">Sources:</span>
                <div className="flex flex-wrap gap-1">
                  {(dataSource === 'envirodata' ? dataSources : hydroData?.data_sources || []).map((source, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-cyan-500/10 text-cyan-400 rounded text-[10px]">
                      {source}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Row 1: Location & Elevation Data */}
            <div className="mb-6">
              <h3 className="text-mono-500 text-[10px] tracking-widest mb-3 uppercase">Site Characteristics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="relative">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    AQUIFER / SOIL TYPE
                    {formData.aquifer_name && <span className="ml-2 text-green-400 text-[10px]">●</span>}
                  </label>
                  <input
                    type="text"
                    name="aquifer_name"
                    value={formData.aquifer_name}
                    onChange={handleInputChange}
                    placeholder="—"
                    className={`w-full px-4 py-3 bg-mono-950 border rounded text-sm focus:outline-none transition-all duration-300 ${
                      formData.aquifer_name
                        ? 'border-green-500/30 text-green-300'
                        : 'border-mono-700 text-mono-600 placeholder-mono-700'
                    }`}
                  />
                </div>

                <div className="relative">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    ELEVATION (ft)
                    {formData.elevation_ft && <span className="ml-2 text-green-400 text-[10px]">●</span>}
                  </label>
                  <input
                    type="number"
                    name="elevation_ft"
                    value={formData.elevation_ft}
                    onChange={handleInputChange}
                    placeholder="—"
                    className={`w-full px-4 py-3 bg-mono-950 border rounded text-sm focus:outline-none transition-all duration-300 ${
                      formData.elevation_ft
                        ? 'border-green-500/30 text-green-300'
                        : 'border-mono-700 text-mono-600 placeholder-mono-700'
                    }`}
                  />
                </div>

                <div className="relative">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    DEPTH TO WATER (ft)
                    {formData.depth_to_water_ft && <span className="ml-2 text-green-400 text-[10px]">●</span>}
                  </label>
                  <input
                    type="number"
                    name="depth_to_water_ft"
                    value={formData.depth_to_water_ft}
                    onChange={handleInputChange}
                    step="0.1"
                    placeholder="—"
                    className={`w-full px-4 py-3 bg-mono-950 border rounded text-sm focus:outline-none transition-all duration-300 ${
                      formData.depth_to_water_ft
                        ? 'border-green-500/30 text-green-300'
                        : 'border-mono-700 text-mono-600 placeholder-mono-700'
                    }`}
                  />
                </div>

                <div className="relative">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    SOIL TYPE
                    {formData.soil_type && <span className="ml-2 text-green-400 text-[10px]">●</span>}
                  </label>
                  <input
                    type="text"
                    name="soil_type"
                    value={formData.soil_type}
                    onChange={handleInputChange}
                    placeholder="—"
                    className={`w-full px-4 py-3 bg-mono-950 border rounded text-sm focus:outline-none transition-all duration-300 ${
                      formData.soil_type
                        ? 'border-green-500/30 text-green-300'
                        : 'border-mono-700 text-mono-600 placeholder-mono-700'
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Row 2: Hydraulic Properties */}
            <div className="mb-6">
              <h3 className="text-mono-500 text-[10px] tracking-widest mb-3 uppercase">Hydraulic Properties</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="relative">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    CONDUCTIVITY (ft/day)
                    {formData.aquifer_k_ft_day && <span className="ml-2 text-green-400 text-[10px]">●</span>}
                  </label>
                  <input
                    type="number"
                    name="aquifer_k_ft_day"
                    value={formData.aquifer_k_ft_day}
                    onChange={handleInputChange}
                    step="0.01"
                    placeholder="—"
                    className={`w-full px-4 py-3 bg-mono-950 border rounded text-sm focus:outline-none transition-all duration-300 ${
                      formData.aquifer_k_ft_day
                        ? 'border-green-500/30 text-green-300'
                        : 'border-mono-700 text-mono-600 placeholder-mono-700'
                    }`}
                  />
                </div>

                <div className="relative">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    POROSITY
                    {formData.aquifer_porosity && <span className="ml-2 text-green-400 text-[10px]">●</span>}
                  </label>
                  <input
                    type="number"
                    name="aquifer_porosity"
                    value={formData.aquifer_porosity}
                    onChange={handleInputChange}
                    step="0.001"
                    min="0"
                    max="1"
                    placeholder="—"
                    className={`w-full px-4 py-3 bg-mono-950 border rounded text-sm focus:outline-none transition-all duration-300 ${
                      formData.aquifer_porosity
                        ? 'border-green-500/30 text-green-300'
                        : 'border-mono-700 text-mono-600 placeholder-mono-700'
                    }`}
                  />
                </div>

                <div className="relative">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    GRADIENT (ft/ft)
                    {formData.hydraulic_gradient && <span className="ml-2 text-green-400 text-[10px]">●</span>}
                  </label>
                  <input
                    type="number"
                    name="hydraulic_gradient"
                    value={formData.hydraulic_gradient}
                    onChange={handleInputChange}
                    step="0.0001"
                    placeholder="—"
                    className={`w-full px-4 py-3 bg-mono-950 border rounded text-sm focus:outline-none transition-all duration-300 ${
                      formData.hydraulic_gradient
                        ? 'border-green-500/30 text-green-300'
                        : 'border-mono-700 text-mono-600 placeholder-mono-700'
                    }`}
                  />
                </div>

                <div className="relative">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    ORGANIC CARBON (foc)
                    {formData.organic_carbon_foc && <span className="ml-2 text-green-400 text-[10px]">●</span>}
                  </label>
                  <input
                    type="number"
                    name="organic_carbon_foc"
                    value={formData.organic_carbon_foc}
                    onChange={handleInputChange}
                    step="0.0001"
                    placeholder="—"
                    className={`w-full px-4 py-3 bg-mono-950 border rounded text-sm focus:outline-none transition-all duration-300 ${
                      formData.organic_carbon_foc
                        ? 'border-green-500/30 text-green-300'
                        : 'border-mono-700 text-mono-600 placeholder-mono-700'
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Row 3: Flow & Transport */}
            <div>
              <h3 className="text-mono-500 text-[10px] tracking-widest mb-3 uppercase">Flow & Transport</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="relative">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    FLOW DIRECTION (deg)
                    {formData.flow_direction_degrees && <span className="ml-2 text-green-400 text-[10px]">●</span>}
                  </label>
                  <input
                    type="number"
                    name="flow_direction_degrees"
                    value={formData.flow_direction_degrees}
                    onChange={handleInputChange}
                    min="0"
                    max="360"
                    placeholder="—"
                    className={`w-full px-4 py-3 bg-mono-950 border rounded text-sm focus:outline-none transition-all duration-300 ${
                      formData.flow_direction_degrees
                        ? 'border-green-500/30 text-green-300'
                        : 'border-mono-700 text-mono-600 placeholder-mono-700'
                    }`}
                  />
                </div>

                <div className="relative">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    GW VELOCITY (ft/day)
                    {formData.groundwater_velocity_ft_day && <span className="ml-2 text-green-400 text-[10px]">●</span>}
                  </label>
                  <input
                    type="number"
                    name="groundwater_velocity_ft_day"
                    value={formData.groundwater_velocity_ft_day}
                    onChange={handleInputChange}
                    step="0.001"
                    placeholder="—"
                    readOnly
                    className={`w-full px-4 py-3 bg-mono-950 border rounded text-sm focus:outline-none transition-all duration-300 ${
                      formData.groundwater_velocity_ft_day
                        ? 'border-cyan-500/30 text-cyan-300 bg-cyan-500/5'
                        : 'border-mono-700 text-mono-600 placeholder-mono-700'
                    }`}
                  />
                  {formData.groundwater_velocity_ft_day && (
                    <span className="absolute right-2 top-9 text-[10px] text-cyan-400">calculated</span>
                  )}
                </div>

                <div className="relative">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    AQUIFER THICKNESS (ft)
                    {formData.aquifer_thickness_ft && <span className="ml-2 text-green-400 text-[10px]">●</span>}
                  </label>
                  <input
                    type="number"
                    name="aquifer_thickness_ft"
                    value={formData.aquifer_thickness_ft}
                    onChange={handleInputChange}
                    step="0.1"
                    placeholder="—"
                    className={`w-full px-4 py-3 bg-mono-950 border rounded text-sm focus:outline-none transition-all duration-300 ${
                      formData.aquifer_thickness_ft
                        ? 'border-green-500/30 text-green-300'
                        : 'border-mono-700 text-mono-600 placeholder-mono-700'
                    }`}
                  />
                </div>

                <div className="relative">
                  <label className="block text-mono-400 text-xs tracking-wider mb-2">
                    TRANSMISSIVITY (ft²/day)
                    {formData.transmissivity_ft2_day && <span className="ml-2 text-green-400 text-[10px]">●</span>}
                  </label>
                  <input
                    type="number"
                    name="transmissivity_ft2_day"
                    value={formData.transmissivity_ft2_day}
                    onChange={handleInputChange}
                    step="0.01"
                    placeholder="—"
                    readOnly
                    className={`w-full px-4 py-3 bg-mono-950 border rounded text-sm focus:outline-none transition-all duration-300 ${
                      formData.transmissivity_ft2_day
                        ? 'border-cyan-500/30 text-cyan-300 bg-cyan-500/5'
                        : 'border-mono-700 text-mono-600 placeholder-mono-700'
                    }`}
                  />
                  {formData.transmissivity_ft2_day && (
                    <span className="absolute right-2 top-9 text-[10px] text-cyan-400">T = K × b</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: Existing Wells (Auto-detected) */}
          <div className={`bg-mono-900 border rounded-lg p-6 transition-all duration-500 ${autoDetectedWells && autoDetectedWells.length > 0 ? 'border-green-500/30' : 'border-mono-800'}`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded transition-colors duration-300 ${autoDetectedWells && autoDetectedWells.length > 0 ? 'bg-green-500/20' : 'bg-mono-800'}`}>
                  <MapPinIcon className={`h-5 w-5 transition-colors duration-300 ${autoDetectedWells && autoDetectedWells.length > 0 ? 'text-green-400' : 'text-mono-400'}`} />
                </div>
                <div>
                  <h2 className="text-mono-100 font-semibold tracking-wider font-orbitron">EXISTING WELLS</h2>
                  <p className="text-mono-600 text-xs">
                    {isFetchingSiteData
                      ? 'Searching USGS, EPA, TCEQ, TWDB, WQP databases...'
                      : autoDetectedWells && autoDetectedWells.length > 0
                        ? `${autoDetectedWells.length} wells found within 0.5 miles`
                        : 'No wells detected yet - enter coordinates above'}
                  </p>
                </div>
              </div>
              {autoDetectedWells && autoDetectedWells.length > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <CheckCircleIcon className="h-4 w-4 text-green-400" />
                  <span className="text-green-400 text-xs tracking-wider font-semibold">{autoDetectedWells.length} FOUND</span>
                </div>
              )}
            </div>

            {/* Wells by source breakdown */}
            {hydroData?.wells_summary?.wells_by_source && (
              <div className="flex flex-wrap gap-2 mb-4">
                {Object.entries(hydroData.wells_summary.wells_by_source).map(([source, count]) => (
                  count > 0 && (
                    <span key={source} className="px-3 py-1.5 bg-mono-800 text-mono-300 text-xs rounded-lg border border-mono-700">
                      <span className="text-mono-500">{source}:</span> <span className="font-semibold">{count}</span>
                    </span>
                  )
                ))}
              </div>
            )}

            {/* Wells grid display */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
              {autoDetectedWells && autoDetectedWells.length > 0 ? (
                autoDetectedWells.slice(0, 12).map((well, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-mono-950 rounded-lg border border-mono-800 hover:border-green-500/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <div>
                        <p className="text-mono-200 font-mono text-sm">{well.well_id}</p>
                        <p className="text-mono-500 text-[10px]">{well.source_database || well.source || 'Unknown'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-mono-300 text-xs">{well.distance_ft?.toFixed(0) || '?'} ft</p>
                      {well.well_depth_ft && <p className="text-mono-500 text-[10px]">{well.well_depth_ft} ft deep</p>}
                    </div>
                  </div>
                ))
              ) : (
                // Empty state placeholders
                Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-mono-950/50 rounded-lg border border-mono-800/50">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-mono-700 rounded-full"></div>
                      <div>
                        <div className="h-4 w-24 bg-mono-800 rounded"></div>
                        <div className="h-3 w-16 bg-mono-800/50 rounded mt-1"></div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="h-3 w-12 bg-mono-800 rounded"></div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {autoDetectedWells && autoDetectedWells.length > 12 && (
              <p className="text-mono-500 text-xs text-center mt-3">+ {autoDetectedWells.length - 12} more wells</p>
            )}

            {/* Nearest well highlight */}
            {hydroData?.wells_summary?.nearest_well_ft && (
              <div className="mt-4 pt-4 border-t border-mono-800 flex items-center gap-2">
                <span className="text-mono-500 text-xs">Nearest well:</span>
                <span className="text-green-400 font-semibold text-sm">{hydroData.wells_summary.nearest_well_ft.toFixed(0)} ft</span>
                <span className="text-mono-600 text-xs">from site center</span>
              </div>
            )}
          </div>

          {/* Section 5: Manual Wells (optional) */}
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-mono-800 rounded">
                  <PlusIcon className="h-5 w-5 text-mono-400" />
                </div>
                <div>
                  <h2 className="text-mono-100 font-semibold tracking-wider font-orbitron">ADDITIONAL WELLS (MANUAL)</h2>
                  <p className="text-mono-600 text-xs">Add wells not found in national databases</p>
                </div>
              </div>
              <button
                type="button"
                onClick={addWell}
                className="flex items-center gap-2 px-3 py-2 bg-mono-800 hover:bg-mono-700 text-mono-300 text-xs tracking-wider transition-colors rounded"
              >
                <PlusIcon className="h-4 w-4" />
                ADD WELL
              </button>
            </div>

            {/* Auto-detect toggle */}
            <div className="flex items-center gap-3 mb-6 p-4 bg-mono-950 rounded-lg border border-mono-800">
              <input
                type="checkbox"
                id="autoDetect"
                checked={autoDetectWells}
                onChange={(e) => setAutoDetectWells(e.target.checked)}
                className="h-4 w-4 rounded border-mono-600 bg-mono-900 text-green-500 focus:ring-green-500"
              />
              <label htmlFor="autoDetect" className="flex-1">
                <span className="text-mono-200 text-sm">Include auto-detected wells in analysis</span>
                <p className="text-mono-600 text-xs mt-1">
                  Wells from USGS NWIS, EPA CERCLIS/RCRA, TCEQ, TWDB, and Water Quality Portal
                </p>
              </label>
            </div>

            {/* Manual wells */}
            {wells.length > 0 && (
              <div className="space-y-4">
                {wells.map((well, index) => (
                  <div key={index} className="p-4 bg-mono-950 rounded-lg border border-mono-800">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-mono-300 text-sm font-semibold">
                        {well.well_id}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeWell(index)}
                        className="text-mono-500 hover:text-mono-300 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-mono-500 text-xs mb-1">Well ID</label>
                        <input
                          type="text"
                          value={well.well_id}
                          onChange={(e) => updateWell(index, 'well_id', e.target.value)}
                          className="w-full px-3 py-2 bg-mono-900 border border-mono-700 rounded text-mono-200 text-sm focus:outline-none focus:border-mono-500"
                        />
                      </div>
                      <div>
                        <label className="block text-mono-500 text-xs mb-1">Distance (ft)</label>
                        <input
                          type="number"
                          value={well.distance_from_source_ft}
                          onChange={(e) => updateWell(index, 'distance_from_source_ft', e.target.value)}
                          placeholder="100"
                          className="w-full px-3 py-2 bg-mono-900 border border-mono-700 rounded text-mono-200 text-sm focus:outline-none focus:border-mono-500"
                        />
                      </div>
                      <div>
                        <label className="block text-mono-500 text-xs mb-1">Direction (deg)</label>
                        <input
                          type="number"
                          value={well.direction_from_source_degrees}
                          onChange={(e) => updateWell(index, 'direction_from_source_degrees', e.target.value)}
                          placeholder="135"
                          className="w-full px-3 py-2 bg-mono-900 border border-mono-700 rounded text-mono-200 text-sm focus:outline-none focus:border-mono-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {wells.length === 0 && !autoDetectWells && (
              <div className="text-center py-8 text-mono-600 text-sm">
                No wells added. Click "Add Well" or enable auto-detect.
              </div>
            )}
          </div>

          {/* Section 5: Analysis Options */}
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-mono-800 rounded">
                <ArrowPathIcon className="h-5 w-5 text-mono-400" />
              </div>
              <div>
                <h2 className="text-mono-100 font-semibold tracking-wider font-orbitron">ANALYSIS OPTIONS</h2>
                <p className="text-mono-600 text-xs">Configure prediction parameters</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-mono-400 text-xs tracking-wider mb-2">
                  PROJECTION PERIOD
                </label>
                <select
                  value={projectionMonths}
                  onChange={(e) => setProjectionMonths(parseInt(e.target.value))}
                  className="w-full px-4 py-3 bg-mono-950 border border-mono-700 rounded text-mono-200 text-sm focus:outline-none focus:border-mono-500"
                >
                  <option value={6}>6 Months</option>
                  <option value={12}>12 Months</option>
                  <option value={24}>24 Months</option>
                  <option value={36}>36 Months</option>
                </select>
              </div>
            </div>

            {/* Info box */}
            <div className="mt-6 p-4 bg-mono-950 rounded-lg border border-mono-800 flex items-start gap-3">
              <InformationCircleIcon className="h-5 w-5 text-mono-500 flex-shrink-0 mt-0.5" />
              <div className="text-mono-500 text-xs">
                <p className="mb-2">
                  POLYGON will automatically fetch data from national databases including:
                </p>
                <ul className="list-disc list-inside space-y-1 text-mono-600">
                  <li>USGS SSURGO soil data</li>
                  <li>USGS 3DEP elevation data</li>
                  <li>EPA CERCLIS and RCRA site data</li>
                  <li>State environmental databases</li>
                  <li>Satellite imagery for NDVI analysis</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/polygon/dashboard')}
              className="px-6 py-3 bg-mono-800 hover:bg-mono-700 text-mono-300 text-xs tracking-widest transition-colors"
            >
              CANCEL
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isFetchingSiteData}
              className="flex items-center gap-2 px-8 py-3 bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-bold tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  GENERATING MODEL...
                </>
              ) : (
                <>
                  <SparklesIcon className="h-4 w-4" />
                  GENERATE
                </>
              )}
            </button>
          </div>
        </StaggeredFadeIn>
      </form>
    </div>
  );
}
